import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';

import { SimpleLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { actions, selectors } from '@elab/core/store/payment';
import { actions as uiActions } from '@elab/core/store/ui';
import { selectors as userSelectors } from '@elab/core/store/user';
import { deferred } from '@elab/core/utils/webComponent';

/**
 * Component is used to show available payment methods from payment store
 *
 * HTML Structure Example
 *
 * <e2-sop-payment-methods>
 * </e2-sop-payment-methods>
 */
export class PaymentMethodSOP extends SimpleLitElement {
    static get properties() {
        return {
            cardList: { type: Array, attribute: false },
            iFrameTimedOut: { type: Array, attribute: false },
            canSaveCard: { type: Boolean, attribute: false },
            isSaveCardUsed: { type: Boolean, attribute: false },
            cardListAvailable: { type: Boolean, attribute: false },
            iframeUrl: { type: String, attribute: false },
            selectedCard: { type: Object, attribute: false },
            userId: { type: String, attribute: false },
            showError: { type: Boolean, attribute: false },
            displayNewCardSection: { type: Boolean, attribute: false },
            payNowActive: { type: Boolean, attribute: false },
            cardHolderName: { type: String, attribute: false },
            cardListLabel: { type: String, attribute: 'card-list-label' },
            newCardLabel: { type: String, attribute: 'new-card-label' },
            saveCardLabel: { type: String, attribute: 'save-card-label' },
            sessionExpiredLabel: { type: String, attribute: 'iframe-session-expired-label' },
            paymentMethodSet: { type: Boolean, attribute: false },
            cardListNotAvailableText: {
                type: String,
                attribute: 'card-list-not-available-text',
            },
            noSaveCardLabel: {
                type: String,
                attribute: 'no-save-card-label',
            },
            cardHolderPlaceHolderLabel: {
                type: String,
                attribute: 'cardholder-placeholder-label',
            },
            cardNumberPlaceHolderLabel: {
                type: String,
                attribute: 'cardnumber-placeholder-label',
            },
            cardCVCPlaceHolderLabel: {
                type: String,
                attribute: 'cvc-placeholder-label',
            },
            cardErrorText: {
                type: String,
                attribute: 'card-error-text',
            },
            cardExpiryText: {
                type: String,
                attribute: 'card-expiry-text',
            },
            timeoutInterval: { type: Number, attribute: false },
            analyticsConfig: { type: String, attribute: 'analytics-config' },
        };
    }

    constructor() {
        super();
        this.selectedCard = null;
        this.saveCard = false;
        this.cardList = [];
        this.cardListLabel = '';
        this.newCardLabel = '';
        this.saveCardLabel = '';
        this.paymentAvailable = true;
        this.displayNewCardSection = false;
        this.iframeUrl = '';
        this.cardHolderPlaceHolderLabel = '';
        this.cardNumberPlaceHolderLabel = '';
        this.cardCVCPlaceHolderLabel = '';
        this.cardHolderName = '';
        this.canSaveCard = true;
        this.cardListAvailable = false;
        this.isSaveCardUsed = true;
        this.showError = false;
        this.iFrameTimedOut = false;
        this.cardExpiryText = '';
        this.iframeSectionId = 'sop-methods-new-credit-card-section';
        this.timeoutInterval = 0;
        this.analyticsConfig = '';
    }

    get cardNumberUrl() {
        return this.iframeUrl
            ? `${this.iframeUrl}/identifier?placeholder=${this.cardNumberPlaceHolderLabel}`
            : '';
    }

    get cvcUrl() {
        return this.iframeUrl
            ? `${this.iframeUrl}/cvc?placeholder=${this.cardCVCPlaceHolderLabel}`
            : '';
    }

    get expiryURL() {
        return this.iframeUrl ? `${this.iframeUrl}/expiry` : '';
    }

    get loggedInUser() {
        return !!this.userId;
    }

    afterConnect() {
        window.addEventListener('message', this.checkValid, false);
        if (this.loggedInUser) {
            this.dispatchEvent(new CustomEvent('get-saved-tokens'));
        }
        if (!this.canSaveCard) {
            this.fetchIFrameURL();
        }

        this.dispatchEvent(
            new CustomEvent('disabled-pay-button', {
                detail: false,
            }),
        );
    }

    updated(_changedProperties) {
        super.updated(_changedProperties);
        if (_changedProperties.has('cardList')) {
            this.displayNewCardSection = false;
        }
    }

    checkValid = form => {
        try {
            const data = JSON.parse(form.data);
            const isValid = data.valid === 'true';
            if (isValid) {
                if (data.response) {
                    const { brand, expiry, identifier } = data.response;
                    const card = {
                        cardType: {
                            code: brand,
                        },
                        expiryYear: expiry.slice(0, 2),
                        expiryMonth: expiry.slice(2, expiry.length),
                        cardNumber: identifier,
                        ...this.selectedCard,
                    };
                    this.dispatchSelectedCard(card);
                    this.showError = !isValid;
                    this.dispatchEvent(
                        new CustomEvent('disabled-pay-button', {
                            detail: !!this.cardHolderName,
                        }),
                    );
                }
            } else {
                if (data.timeout === 0) {
                    this.iFrameTimedOut = true;
                }
                if (!(data.event === 'onfocus' || data.event === 'onblur' || data.brandChange)) {
                    this.showError = !isValid;
                }
            }
        } catch (e) {
            // send error info ??
        }
    };

    beforeDisconnect() {
        this.dispatchEvent(new CustomEvent('reset-card-selection'));
        this.dispatchEvent(new CustomEvent('reset-sop-data'));
        window.removeEventListener('message', this.checkValid, false);
    }

    validCard(card) {
        return !card.expired;
    }

    handleCardSelection(card) {
        this.selectedCard && card.id === this.selectedCard.id ? false : this.onCardSelection(card);
    }

    toggleSaveCardUsed(isUsed) {
        this.isSaveCardUsed = isUsed;
        this.showError = false;
        this.iFrameTimedOut = false;
        this.dispatchEvent(new CustomEvent('save-card-used', { detail: isUsed }));
    }

    onCardSelection(card) {
        this.toggleSaveCardUsed(true);
        this.displayNewCardSection = false;
        this.fetchIFrameURL(card.id);
        this.onCardHolderName({ target: { value: card.accountHolderName } });
        this.dispatchSelectedCard(card);
        this.displayNewCardSection = true;
        this.scrollToIframe();
    }

    onRemoveCardClick(event, card) {
        event.stopImmediatePropagation();
        this.dispatchEvent(new CustomEvent('reset-errors'));
        this.dispatchEvent(
            new CustomEvent('set-remove-card', {
                detail: {
                    type: card.cardType.code,
                    number: card.cardNumber,
                    cardId: card.id,
                },
            }),
        );
        this.dispatchEvent(new CustomEvent('show-remove-confirmation'));
    }

    dispatchSelectedCard(card) {
        this.dispatchEvent(new CustomEvent('set-selected-card', { detail: card }));
        this.dispatchEvent(new CustomEvent('disabled-pay-button', { detail: false }));
    }

    onCardHolderName({ target }) {
        if (target.value) {
            this.cardHolderName = target.value.toUpperCase();
            this.dispatchEvent(
                new CustomEvent('credit-card-name', {
                    detail: this.cardHolderName,
                }),
            );
            if (this.selectedCard) {
                this.dispatchEvent(
                    new CustomEvent('disabled-pay-button', {
                        detail: true,
                    }),
                );
            }
        } else {
            this.dispatchEvent(
                new CustomEvent('disabled-pay-button', {
                    detail: false,
                }),
            );
        }
    }

    get getIframeSection() {
        return document.getElementById(this.iframeSectionId);
    }

    scrollToIframe = deferred(() => {
        const iframeElement = this.getIframeSection;

        if (iframeElement) {
            this.dispatchEvent(
                new CustomEvent('scroll-to-iframe', {
                    detail: {
                        element: iframeElement,
                    },
                }),
            );
        }
    }, 10);

    toggleDisplayNewCardSection() {
        const selectedCard = this.selectedCard && this.selectedCard.id;
        this.toggleSaveCardUsed(false);
        this.resetCardSelection();
        this.displayNewCardSection = !this.displayNewCardSection || !!selectedCard;
        if (this.displayNewCardSection) {
            this.fetchIFrameURL('');
            this.scrollToIframe();
        } else {
            this.dispatchEvent(new CustomEvent('reset-sop-data'));
        }
        this.dispatchEvent(new CustomEvent('disabled-pay-button', { detail: false }));
    }

    resetCardSelection() {
        this.cardHolderName = '';
        // remove any card item selection if any
        this.showError = false;
        this.dispatchEvent(new CustomEvent('reset-errors'));
        this.dispatchEvent(new CustomEvent('reset-card-selection'));
    }

    fetchIFrameURL = (id = '') => {
        if (this.paymentMethodSet) {
            clearTimeout(this.timeoutInterval);
            this.dispatchEvent(new CustomEvent('get-iFrame-url', { detail: id }));
        } else {
            this.timeoutInterval = setTimeout(this.fetchIFrameURL, 100);
        }
    };

    getCardItemClasses(card) {
        return classMap({
            'sop-methods__cards__item': true,
            'sop-methods__cards__item--disabled': !this.validCard(card),
        });
    }

    getRadioButtonClasses(card) {
        return classMap({
            'sop-methods__cards__item--mark': true,
            'sop-methods__cards__item--mark--checked':
                this.selectedCard && card.id === this.selectedCard.id,
            'sop-methods__cards__item--mark--disabled': !this.validCard(card),
        });
    }

    getRadioButtonHolderClasses(card) {
        return classMap({
            'sop-methods__cards__item--circle': true,
            'sop-methods__cards__item--circle--expired': !this.validCard(card),
        });
    }

    toggleSaveCard() {
        this.saveCard = !this.saveCard;
        this.dispatchEvent(new CustomEvent('save-card-flag', { detail: this.saveCard }));
    }

    renderCardLogo(type = 'default') {
        type = type.toLowerCase();
        return html`
            <div class="${`sop-methods__cards__item__card--${type}`}"></div>
        `;
    }

    formatCardDate(card) {
        return `${card.expiryMonth}/${card.expiryYear}`;
    }

    renderSaveCardToggler() {
        if (this.canSaveCard && !this.isSaveCardUsed) {
            const template = html`
                <e2-toggler
                    class="sop-methods__cards--new__credit--checkbox"
                    display-as-checkbox
                    name="saveCard"
                    ?hidden="${!this.canSaveCard || this.isSaveCardUsed}"
                    .checked="${this.saveCard}"
                    @change="${this.toggleSaveCard}"
                >
                    <div v-slot="toggler__label">
                        ${this.saveCardLabel} &nbsp;<i class="icon icon-help"></i>
                    </div>
                </e2-toggler>
            `;
            return this.analyticsConfig !== ''
                ? html`
                      <e2-click-tracking
                          analytics-config=${this.analyticsConfig}
                          type="Payment-save_card"
                      >
                          ${template}
                      </e2-click-tracking>
                  `
                : template;
        }
        return null;
    }

    renderIFrameSection() {
        return this.displayNewCardSection || !this.loggedInUser || !this.canSaveCard
            ? html`
                  <div class="sop-methods__cards--divider"></div>
                  <div class="sop-methods__cards--new__credit" id="${this.iframeSectionId}">
                      <div class="sop-methods__cards--new__credit--image">
                          <div class="sop-methods__cards__logo">
                              <span class="sop-methods__cards__chip-icon"></span>
                              <span class="sop-methods__cards__wi-fi-icon"></span>
                          </div>
                          <div
                              class="sop-methods__cards-overlay${this.iFrameTimedOut
                                  ? '--active'
                                  : ''}"
                          >
                              <i class="icon icon-close"></i>
                              <span
                                  class="sop-methods__cards-overlay-text${this.iFrameTimedOut
                                      ? '--visible'
                                      : ''}"
                                  >${this.sessionExpiredLabel}</span
                              >
                          </div>
                          <div class="sop-methods__cards--new__credit--image__custom--row">
                              <input
                                  id="identifier2"
                                  autocomplete="off"
                                  class="sop-methods__cards--new__credit--image__custom--row--input"
                                  name="cardNumber"
                                  .value="${this.cardHolderName}"
                                  placeholder="${this.cardHolderPlaceHolderLabel}"
                                  @input="${this.onCardHolderName}"
                              />
                          </div>
                          <iframe
                              id="cardFrame-number"
                              src="${this.cardNumberUrl}"
                              title="card-iFrame"
                          ></iframe>
                          <div class="sop-methods__cards-row">
                              <iframe
                                  id="card-frame-expiry"
                                  src="${this.expiryURL}"
                                  title="card-iFrame"
                              ></iframe>
                              <iframe
                                  id="card-frame-cvc"
                                  src="${this.cvcUrl}"
                                  title="card-iFrame"
                              ></iframe>
                          </div>
                      </div>
                      ${this.renderCardError()} ${this.renderSaveCardToggler()}
                  </div>
              `
            : null;
    }

    renderCardError() {
        return this.showError
            ? html`
                  <div class="sop-methods__error">
                      <span class="sop-methods__error-icon"></span>
                      ${this.cardErrorText}
                  </div>
              `
            : '';
    }

    renderCardExpired(card) {
        return !this.validCard(card)
            ? html`
                  <span class="sop-methods__cards__item__card--expired">
                      ${this.cardExpiryText}
                  </span>
              `
            : '';
    }

    renderCardItem = card => {
        const template = html`
            <label class="${this.getCardItemClasses(card)}">
                <div class="${this.getRadioButtonHolderClasses(card)}">
                    <span class="${this.getRadioButtonClasses(card)}"></span>
                    <input
                        class="sop-methods__cards__item--input a11y-form__radio"
                        type="radio"
                        value="${card.id}"
                        ?checked="${this.selectedCard && card.id === this.selectedCard.id}"
                        ?disabled="${!this.validCard(card)}"
                        @click="${() => this.handleCardSelection(card)}"
                    />
                </div>
                <div class="sop-methods__cards__item__card">
                    ${this.renderCardLogo(card.cardType.code)}

                    <span class="sop-methods__cards__item__card--label">
                        ${card.cardNumber}
                    </span>

                    <div class="sop-methods__cards-item-column">
                        <span class="sop-methods__cards__item__card--label"
                            >${this.formatCardDate(card)}</span
                        >
                        ${this.renderCardExpired(card)}
                    </div>
                </div>
            </label>
        `;
        return this.analyticsConfig !== ''
            ? html`
                  <div class="sop-methods__cards__item-holder">
                      <e2-click-tracking
                          analytics-config=${this.analyticsConfig}
                          type="Payment-select_credit_card"
                          class="sop-methods__cards__item-label-wrap"
                      >
                          ${template}
                      </e2-click-tracking>
                      <e2-click-tracking
                          analytics-config=${this.analyticsConfig}
                          type="Payment-remove_credit_card"
                      >
                          <div
                              id="removeBtn"
                              class="sop-methods__cards__item__card__btn--remove"
                              @click="${e => this.onRemoveCardClick(e, card)}"
                          />
                      </e2-click-tracking>
                  </div>
              `
            : html`
                  <div class="sop-methods__cards__item-holder">
                      ${template}
                      <div
                          id="removeBtn"
                          class="sop-methods__cards__item__card__btn--remove"
                          @click="${e => this.onRemoveCardClick(e, card)}"
                      />
                  </div>
              `;
    };

    renderCardList() {
        if (this.cardList.length > 0) {
            const uniqueCards = [];
            this.cardList.forEach(card => {
                const index = uniqueCards.findIndex(item => item.id === card.id);
                if (index <= -1) {
                    uniqueCards.push(card);
                }
            });
            return html`
                ${uniqueCards.map(this.renderCardItem)}
            `;
        }
        return null;
    }

    renderNoCardText() {
        return html`
            <div class="sop-methods__cards-text">
                <span class="sop-methods__info-icon"></span>
                ${this.cardListAvailable ? this.cardListNotAvailableText : this.noSaveCardLabel}
            </div>
        `;
    }

    renderGuestView() {
        return this.renderIFrameSection();
    }

    renderLoggedInUserView() {
        return html`
            <span class="sop-methods__title--label" ?hidden="${!this.cardListAvailable}"
                >${this.cardListLabel}</span
            >

            <div class="sop-methods__cards">
                ${this.cardListAvailable && this.cardList.length > 0
                    ? this.renderCardList()
                    : this.renderNoCardText()}
            </div>
            <div class="sop-methods__cards--new" @click="${this.toggleDisplayNewCardSection}">
                ${this.analyticsConfig !== ''
                    ? html`
                          <e2-click-tracking
                              analytics-config=${this.analyticsConfig}
                              type="Payment-add_card"
                          >
                              <span class="sop-methods__cards--new__title--label">
                                  ${this.newCardLabel}
                              </span>
                          </e2-click-tracking>
                      `
                    : html`
                          <span class="sop-methods__cards--new__title--label"
                              >${this.newCardLabel}</span
                          >
                      `}
            </div>
            ${this.renderIFrameSection()}
        `;
    }

    render() {
        this.cardList = this.cardList ? this.cardList : [];
        return html`
            <div class="sop-methods">
                ${this.loggedInUser && this.canSaveCard
                    ? this.renderLoggedInUserView()
                    : this.renderGuestView()}
            </div>
        `;
    }
}

const mapStateToProps = state => {
    return {
        cardList: selectors.getSavedCards(state),
        paymentMethodSet: selectors.getPaymentMethodSet(state),
        selectedCard: selectors.getSelectedCard(state),
        iframeUrl: selectors.getIFrameURL(state),
        payNowActive: selectors.getPayNowActive(state),
        cardListAvailable: selectors.getSavedCardsAvailable(state),
        canSaveCard: selectors.getTokenizationAllowed(state),
        userId: userSelectors.getUserId(state),
    };
};

const mapDispatchToEvents = dispatch => {
    return {
        'get-saved-tokens': () => dispatch(actions.getSavedCards()),
        'get-iFrame-url': e => dispatch(actions.fetchIFrameURL({ id: e.detail })),
        'set-selected-card': e => dispatch(actions.setSelectedCard(e.detail)),
        'save-card-flag': e => dispatch(actions.setSaveCardFlag(e.detail)),
        'credit-card-name': e => dispatch(actions.setCardHolderName(e.detail)),
        'disabled-pay-button': e => dispatch(actions.payNowActive(!e.detail)),
        'reset-card-selection': () => dispatch(actions.resetCardSelection()),
        'set-payment-method': e => dispatch(actions.setPaymentMethod(e.detail)),
        'save-card-used': e => dispatch(actions.setSaveCardUsed(e.detail)),
        'reset-errors': () => dispatch(actions.resetErrors()),
        'set-remove-card': ({ detail }) => dispatch(actions.setRemoveCard(detail)),
        'reset-sop-data': () => dispatch(actions.resetSopData()),
        'show-remove-confirmation': () =>
            dispatch(
                uiActions.openDialog({
                    name: 'remove-credit-card-payment-popup',
                }),
            ),
        'scroll-to-iframe': ({ detail }) =>
            dispatch(
                uiActions.scrollToCenter({
                    element: detail.element,
                }),
            ),
    };
};

export default () =>
    customElements.define(
        'e2-sop-payment-methods',
        connect(mapStateToProps, mapDispatchToEvents)(PaymentMethodSOP),
    );
